import moment from 'moment';
import { createReducer, INITIAL_STATE } from '@shared/redux';
import { LOAD_STATE } from '@shared/axios';
import { getCampaignsReportsConfig } from '@domain/campaigns';
import { API_DATE_FORMAT } from '@shared/utils/date';
import { AxiosActionPayload, AxiosResponseAction } from '@shared/axios/axios.types';
import {
    BBMConfig,
    BBMConfigWithStats,
    BudgetManagerState,
    CampaignStatsItem,
    CampaignStatsPayload,
    TagGroupPayload,
} from '@domain/dashboard/budget-manager/budget-manager.types';
import { ConversionTypes } from '../conversion-types';
import { BUDGET_MANAGER_SORT_STATUS } from './budget-manager.constants';
import { createAPIConfigForUpdateTagGroup } from './budget-manager.api';
import { getIneligibleReason, hasBBMTagsForObject, isObjectEquals } from './budget-manager.utils';

export const TOGGLE_VISIBILITY_BBM_MODAL = '@dashboard/budget-manager/TOGGLE_VISIBILITY_BBM_MODAL';

export const FETCH_CLUSTER_CONFIG_REQUEST_CALLED = '@dashboard/budget-manager/FETCH_CLUSTER_CONFIG_REQUEST_CALLED';
export const FETCH_CLUSTER_CONFIG_REQUEST_SUCCEEDED = '@dashboard/budget-manager/FETCH_CLUSTER_CONFIG_REQUEST_SUCCEEDED';
export const FETCH_CLUSTER_CONFIG_REQUEST_FAILED = '@dashboard/budget-manager/FETCH_CLUSTER_CONFIG_REQUEST_FAILED';

export const SAVE_CLUSTER_CONFIG_REQUEST_CALLED = '@dashboard/budget-manager/SAVE_CLUSTER_CONFIG_REQUEST_CALLED';
export const SAVE_CLUSTER_CONFIG_REQUEST_SUCCEEDED = '@dashboard/budget-manager/SAVE_CLUSTER_CONFIG_REQUEST_SUCCEEDED';
export const SAVE_CLUSTER_CONFIG_REQUEST_FAILED = '@dashboard/budget-manager/SAVE_CLUSTER_CONFIG_REQUEST_FAILED';

export const SET_OBJECT_TAG = '@dashboard/budget-manager/SET_OBJECT_TAG';

export type FetchClusterConfigRequestSucceeded = AxiosResponseAction<
    typeof FETCH_CLUSTER_CONFIG_REQUEST_SUCCEEDED,
    CampaignStatsPayload,
    AxiosActionPayload<ReturnType<typeof fetchClusterConfig>>
    >;

export type FetchClusterConfigRequestFailed = AxiosResponseAction<
    typeof FETCH_CLUSTER_CONFIG_REQUEST_FAILED,
    CampaignStatsPayload,
    AxiosActionPayload<ReturnType<typeof fetchClusterConfig>>
    >;

export type SaveClusterConfigRequestSucceeded = AxiosResponseAction<
    typeof SAVE_CLUSTER_CONFIG_REQUEST_SUCCEEDED,
    TagGroupPayload,
    AxiosActionPayload<ReturnType<typeof saveClusterConfig>>
    >;

export type SaveClusterConfigRequestFailed = AxiosResponseAction<
    typeof SAVE_CLUSTER_CONFIG_REQUEST_FAILED,
    TagGroupPayload,
    AxiosActionPayload<ReturnType<typeof saveClusterConfig>>
    >;

export const getClustersConfig = state => state.dashboard.budgetManager.clusterConfig;

export const toggleVisibilityBudgetManagerModal = (value: boolean = null) => ({
    type: TOGGLE_VISIBILITY_BBM_MODAL,
    payload: { value },
});

export const fetchClusterConfig = ({
    clientId,
    groupId,
}) => ({
    type: FETCH_CLUSTER_CONFIG_REQUEST_CALLED,
    actionTypes: {
        success: FETCH_CLUSTER_CONFIG_REQUEST_SUCCEEDED,
        fail: FETCH_CLUSTER_CONFIG_REQUEST_FAILED,
    },
    payload: { clientId, groupId },
    config: getCampaignsReportsConfig({
        customerType: 'clients',
        customerId: clientId,
        startDate: moment().subtract(31, 'day').format(API_DATE_FORMAT),
        endDate: moment().format(API_DATE_FORMAT),
        isNewOverview: true,
        conversionType: ConversionTypes.PURCHASE,
    }),
});

export const setObjectTag = newClusterConfig => ({
    type: SET_OBJECT_TAG,
    config: newClusterConfig,
});

export const saveClusterConfig = ({
    clientId,
    groupId,
    objects,
}: BBMConfig) => ({
    type: SAVE_CLUSTER_CONFIG_REQUEST_CALLED,
    actionTypes: {
        success: SAVE_CLUSTER_CONFIG_REQUEST_SUCCEEDED,
        fail: SAVE_CLUSTER_CONFIG_REQUEST_FAILED,
    },
    payload: { clientId, groupId, objects },
    config: createAPIConfigForUpdateTagGroup({
        clientId,
        groupId,
        isFbTaggedLevelAdset: true,
        name: 'Budget Manager Automation',
        platform: 'client',
        taggedCampaigns: objects
            .filter(objs => objs.tag?.label)
            .filter(objs => !objs.stats.ineligibleReason)
            .map(obj => ({
                account_id: obj.accountId,
                campaign_id: obj.campaignId,
                sublevel_id: obj.subLevelId,
                platform: obj.platform,
                tags: [obj.tag.label],
            })),
    }),
});

export const initialState: BudgetManagerState = {
    clusterConfig: {
        data: {
            clientId: null,
            groupId: null,
            objects: [],
        },
        loadState: LOAD_STATE.LOADING,
        error: null,
    },
    isBudgetManagerModalOpen: false,
};

const updateObjects = (data: BBMConfigWithStats, config: BBMConfig) => {
    if (!data) return data;

    const objects = data.objects.map((object) => {
        const updatedObject = config.objects.find(isObjectEquals(object)) || { tag: undefined };
        return {
            ...object,
            tag: updatedObject.tag,
        };
    });
    return { ...data, objects };
};

const convertCampaignStatsItemToClusterConfigObject = (
    groupId: number,
    item: CampaignStatsItem,
) => ({
    accountId: item.account_id,
    campaignId: item.campaign_id,
    subLevelId: item.sublevel_id,
    platform: item.platform,
    tag: (item.tags || [])
        .filter(tag => tag.group_id === groupId && tag.tag !== 'untagged')
        .map(tag => tag.tag)[0],
    stats: {
        bidStrategyType: item.bid_strategy_type,
        objective: item.objective,
        campaignName: item.campaign_name || item.name,
        adsetName: item.name,
        level: item.level,
        status: item.status,
        effectiveStatus: item?.['effective_status']?.toLocaleLowerCase(),
        ineligibleReason: getIneligibleReason(item),
        cost: item.cost,
    },
});

const convertCampaignStatsToClusterConfig = (
    data: CampaignStatsItem[],
    { clientId, groupId },
) => ({
    clientId,
    groupId,
    objects: data
        .filter(item => item.impressions > 0 || hasBBMTagsForObject(item.tags))
        .map(
            item => convertCampaignStatsItemToClusterConfigObject(groupId, item),
        )
        .sort(item => BUDGET_MANAGER_SORT_STATUS[item.stats.status]),
});

export const budgetManagerReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [FETCH_CLUSTER_CONFIG_REQUEST_CALLED]: (state: BudgetManagerState) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            loadState: LOAD_STATE.LOADING,
            error: null,
        },
    }),
    [FETCH_CLUSTER_CONFIG_REQUEST_SUCCEEDED]: (
        state: BudgetManagerState,
        action: FetchClusterConfigRequestSucceeded,
    ) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            data: convertCampaignStatsToClusterConfig(
                action.payload.data?.items,
                action.requestAction.payload,
            ),
            loadState: LOAD_STATE.SUCCESS,
            error: null,
        },
    }),
    [FETCH_CLUSTER_CONFIG_REQUEST_FAILED]: (
        state: BudgetManagerState,
        action: FetchClusterConfigRequestFailed,
    ) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            loadState: LOAD_STATE.FAIL,
            error: action.payload,
        },
    }),
    [SAVE_CLUSTER_CONFIG_REQUEST_CALLED]: (state: BudgetManagerState) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            loadState: LOAD_STATE.LOADING,
            error: null,
        },
    }),
    [SAVE_CLUSTER_CONFIG_REQUEST_SUCCEEDED]: (
        state: BudgetManagerState,
        action: SaveClusterConfigRequestSucceeded,
    ) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            data: updateObjects(state.clusterConfig.data, action.requestAction.payload),
            loadState: LOAD_STATE.SUCCESS,
            error: null,
        },
    }),
    [SAVE_CLUSTER_CONFIG_REQUEST_FAILED]: (
        state: BudgetManagerState,
        action: SaveClusterConfigRequestFailed,
    ) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            loadState: LOAD_STATE.FAIL,
            error: action.payload,
        },
    }),
    [TOGGLE_VISIBILITY_BBM_MODAL]: (state: BudgetManagerState, action) => ({
        ...state,
        isBudgetManagerModalOpen: typeof action.payload.value === 'boolean'
            ? action.payload.value
            : !state.isBudgetManagerModalOpen,
    }),
    [SET_OBJECT_TAG]: (state: BudgetManagerState, action) => ({
        ...state,
        clusterConfig: {
            ...state.clusterConfig,
            data: {
                ...state.clusterConfig.data,
                objects: action.config,
            },
        },
    }),
});
